import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Plus, UserPlus, Hammer, Pencil, Trash2,
  AlertTriangle, ShieldCheck, CornerUpLeft, X, Camera, SearchX, Sparkles,
  Package, CheckCircle2, Wrench, Layers, History,
} from "lucide-react";
import MarketplaceEmptyState, { MarketplaceLoadingSpinner } from "../Marketplace/MarketplaceEmptyState";
import AssignEquipmentModal from "./AssignEquipmentModal";
import ServiceHistoryModal from "./ServiceHistoryModal";
import CategoryManagerModal from "./CategoryManagerModal";
import DeletePopup from "../../../atoms/Dashboard/Folder/DeletePopup";
import { showSuccessToast, showErrorToast } from "../../../atoms/Utlis/Toast";
import {
  EquipmentItem,
  getEquipmentItems,
  getEquipmentDashboard,
  deleteEquipmentItem,
  getCategories,
  Category,
  getReminders,
  dismissReminder
} from "../../../../Redux/ApiCalls/Dashboard/EquipmentAPI";
import { usePermissions } from "../../../../hooks/usePermissions";
import PermissionGate from "../../../atoms/Utlis/PermissionGate";
import { Equipment, typeMap, statusMap } from "./EquipmentConstants";
import { useAppSelector } from "../../../../Redux/Hooks";
import {
  ManageCategoriesBtn,
  AlertStripeContainer,
  AlertStripe,
  AlertContent,
  DismissBtn,
  PageContainer,
  Hero,
  HeroGlow,
  HeroContent,
  HeroText,
  HeroBadge,
  HeaderActions,
  PageTitle,
  PageSubtitle,
  Panel,
  PanelTop,
  TableSection,
  Divider,
  Toolbar,
  SearchControl,
  FiltersSelectRow,
  Select,
  StatsGrid,
  WalletHero,
  WalletIconWrap,
  WalletHeroBody,
  WalletHeroLabel,
  WalletHeroValue,
  StatCard,
  StatIcon,
  StatBody,
  StatValue,
  StatLabel,
  AlertBanner,
  AlertIconWrap,
  AlertTitle,
  AlertDesc,
  StatusBadge,
  EntriesControl,
  EntriesSelect,
  FooterRow,
  FooterText,
  Pagination,
  PageButton,
  InventoryGrid,
  GearCard,
  GearCardTop,
  GearTitleBlock,
  GearTitle,
  GearSubtitle,
  GearMetaLine,
  SoftChip,
  StockBlock,
  StockTrack,
  StockFill,
  StockStats,
  StockStat,
  StockStatLabel,
  StockStatValue,
  AssignBlock,
  AssignAvatar,
  AssignText,
  AssignPrimary,
  AssignSecondary,
  AlertDot,
  GearCardActions,
  PrimaryActionBtn,
  SecondaryActions,
  QuietIconBtn,
  SectionHead,
} from "./EquipmentListPageStyles";
import {
  ListTitle,
  CountBadge,
} from "../../../atoms/Dashboard/CustomerTransaction/transactionTableStyles";
import { SubmitButton } from "../../../molecules/Dashboard/Member/memberFormStyles";

export type { Equipment } from "./EquipmentConstants";

const statusTone: Record<string, string> = {
  Available: "#31a66a",
  Assigned: "#35b7c8",
  "Under Service": "#f09a3e",
  Sold: "#737373",
  Retired: "#e44862",
};

const formatListDate = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value.includes("T") ? value : `${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

export const mapToFrontend = (item: EquipmentItem): Equipment => {
  const totalQuantity = item.total_quantity ?? item.quantity ?? 1;
  const totalAssigned = item.total_assigned ?? 0;
  const remainingTotal = item.remaining_total ?? totalQuantity - totalAssigned;
  const availableSerialNumbers =
    item.available_serial_numbers ||
    item.items?.filter((u) => u.status === "available" && u.serial_number).map((u) => u.serial_number!) ||
    [];
  const assignedSerialNumbers =
    item.assigned_serial_numbers ||
    item.items?.filter((u) => u.status === "assigned" && u.serial_number).map((u) => u.serial_number!) ||
    [];

  // Prefer unit-level occupancy for list status when partially assigned
  let displayStatus = statusMap[item.status] || "Available";
  if (totalAssigned > 0 && remainingTotal > 0 && displayStatus === "Available") {
    displayStatus = "Assigned";
  } else if (totalAssigned > 0 && remainingTotal === 0) {
    displayStatus = "Assigned";
  }

  return {
    id: String(item.id),
    name: item.name,
    category: item.category_name || String(item.category),
    brand: item.brand,
    model: item.model,
    serialNumber: item.serial_number || availableSerialNumbers[0] || assignedSerialNumbers[0] || "",
    quantity: totalQuantity,
    totalQuantity,
    totalAssigned,
    remainingTotal,
    assignedSerialNumbers,
    availableSerialNumbers,
    units:
      item.items?.map((u) => ({
        id: u.id,
        serialNumber: u.serial_number || "",
        status: u.status,
        assignment: u.assignment,
      })) || [],
    type: typeMap[item.ownership_type] || "Studio Owned",
    price: Number(item.purchase_price) || 0,
    status: displayStatus,
    purchaseDate: item.purchase_date || "",
    purchaseVendor: item.purchase_vendor || "",
    warrantyStart: item.warranty_start_date || "",
    warrantyExpiry: item.warranty_expiry_date || "",
    lastServiceDate: item.last_service_date || "",
    nextServiceDueDate: item.next_service_date || "",
    serviceNotes: item.service_notes || "",
    assignedMember: item.active_assignment?.team_member_name || undefined,
    assignedMemberId: item.active_assignment?.team_member
      ? String(item.active_assignment.team_member)
      : undefined,
    assignmentDate: item.active_assignment?.assigned_at
      ? item.active_assignment.assigned_at.split("T")[0]
      : undefined,
    expectedReturnDate: item.active_assignment?.expected_return_at
      ? item.active_assignment.expected_return_at.split("T")[0]
      : undefined,
    branch_id: item.branch_id,
    branch_name: item.branch_name,
  };
};

const EquipmentListPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedBranch } = useAppSelector((state) => state.auth);
  const { hasPermission, hasAnyPermission } = usePermissions();
  const canCreate = hasPermission("equipment.create");
  const canUpdate = hasPermission("equipment.update");
  const canDelete = hasPermission("equipment.delete");
  const canManageCategories = hasAnyPermission(["equipment.update", "equipment.create"]);
  const [equipmentList, setEquipmentList] = useState<Equipment[]>([]);
  const [categoriesList, setCategoriesList] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    available: 0,
    assigned: 0,
    underService: 0,
    warrantyExpiringSoon: 0,
    serviceOverdue: 0
  });

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [entries, setEntries] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [assignTarget, setAssignTarget] = useState<Equipment | null>(null);
  const [serviceTarget, setServiceTarget] = useState<Equipment | null>(null);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [reminders, setReminders] = useState<any[]>([]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const apiStats = await getEquipmentDashboard({ branch_id: selectedBranch?.id });
      setStats({
        total: apiStats.total_equipment || 0,
        available: apiStats.equipment_available || 0,
        assigned: apiStats.equipment_assigned || 0,
        underService: apiStats.equipment_under_service || 0,
        warrantyExpiringSoon: apiStats.warranty_expiring_soon || 0,
        serviceOverdue: apiStats.overdue_returns || 0,
      });

      const apiCats = await getCategories();
      setCategoriesList(apiCats.results || []);

      const apiItems = await getEquipmentItems({ page_size: 100, branch_id: selectedBranch?.id });
      const mapped = (apiItems.results || []).map(mapToFrontend);
      setEquipmentList(mapped);

      try {
        const apiReminders = await getReminders({ status: "pending", branch_id: selectedBranch?.id });
        const results = apiReminders.results || apiReminders || [];
        const todayStr = new Date().toLocaleDateString('en-CA');
        const dueReminders = results.filter((r: any) => r.reminder_date <= todayStr);
        setReminders(dueReminders);
      } catch (remError) {
        console.error("Failed to fetch reminders:", remError);
      }
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to load equipment data");
    } finally {
      setLoading(false);
    }
  };

  const handleDismissReminder = async (id: string) => {
    try {
      await dismissReminder(id);
      setReminders(prev => prev.filter(r => r.id !== id));
      showSuccessToast("Reminder dismissed");
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to dismiss reminder");
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [selectedBranch?.id]);

  const filteredList = useMemo(() => {
    return equipmentList.filter(e => {
      const matchesSearch =
        e.name.toLowerCase().includes(search.toLowerCase()) ||
        e.brand.toLowerCase().includes(search.toLowerCase()) ||
        e.model.toLowerCase().includes(search.toLowerCase()) ||
        e.serialNumber.toLowerCase().includes(search.toLowerCase()) ||
        e.assignedSerialNumbers.some((s) => s.toLowerCase().includes(search.toLowerCase())) ||
        e.availableSerialNumbers.some((s) => s.toLowerCase().includes(search.toLowerCase()));

      const matchesCategory = !category || e.category === category;
      const matchesType = !type || e.type === type;
      const matchesStatus = !status || e.status === status;

      return matchesSearch && matchesCategory && matchesType && matchesStatus;
    });
  }, [equipmentList, search, category, type, status]);

  const totalFiltered = filteredList.length;
  const totalPages = Math.ceil(totalFiltered / entries) || 1;
  const hasActiveFilters = !!(search || category || type || status);
  const isEmptyInventory = equipmentList.length === 0;
  const paginatedList = useMemo(() => {
    const start = (currentPage - 1) * entries;
    return filteredList.slice(start, start + entries);
  }, [filteredList, currentPage, entries]);

  const handleDelete = (id: string, name: string) => {
    setDeleteTarget({ id, name });
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteEquipmentItem(deleteTarget.id);
      showSuccessToast("Equipment deleted successfully");
      setDeleteTarget(null);
      fetchAllData();
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to delete equipment");
      setDeleteTarget(null);
    }
  };

  return (
    <PageContainer>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroText>
            <div className="flex flex-col">
              <HeroBadge>
                <Sparkles size={13} />
                Gear management
              </HeroBadge>
              <PageTitle className="linear-text">Equipment Inventory</PageTitle>
              <PageSubtitle>
                Manage photography gear, team assignments, and servicing schedules.
              </PageSubtitle>
            </div>
          </HeroText>
          <HeaderActions>
            {canManageCategories && (
              <ManageCategoriesBtn onClick={() => setShowCategoriesModal(true)}>
                <Layers size={16} /> Manage categories
              </ManageCategoriesBtn>
            )}
            <PermissionGate permission="equipment.create">
              <SubmitButton
                type="button"
                $ready
                onClick={() => navigate("/dashboard/equipment/new")}
                style={{ minWidth: 168, height: 46 }}
              >
                <Plus size={16} /> Add equipment
              </SubmitButton>
            </PermissionGate>
          </HeaderActions>
        </HeroContent>
      </Hero>

      {reminders.length > 0 && (
        <AlertStripeContainer>
          {reminders.map((reminder) => (
            <AlertStripe key={reminder.id}>
              <AlertContent>
                <AlertTriangle size={18} style={{ flexShrink: 0 }} />
                <span>
                  <strong>{reminder.title}:</strong> {reminder.message}
                </span>
              </AlertContent>
              <DismissBtn onClick={() => handleDismissReminder(reminder.id)} title="Dismiss reminder">
                <X size={16} />
              </DismissBtn>
            </AlertStripe>
          ))}
        </AlertStripeContainer>
      )}

      <StatsGrid>
        <WalletHero>
          <WalletIconWrap>
            <Package size={16} />
          </WalletIconWrap>
          <WalletHeroBody>
            <WalletHeroValue>{stats.total}</WalletHeroValue>
            <WalletHeroLabel>Total equipment</WalletHeroLabel>
          </WalletHeroBody>
        </WalletHero>

        <StatCard $tone="#31a66a">
          <StatIcon $tone="#31a66a">
            <CheckCircle2 size={16} />
          </StatIcon>
          <StatBody>
            <StatValue>{stats.available}</StatValue>
            <StatLabel>Available</StatLabel>
          </StatBody>
        </StatCard>

        <StatCard $tone="#35b7c8">
          <StatIcon $tone="#35b7c8">
            <UserPlus size={16} />
          </StatIcon>
          <StatBody>
            <StatValue>{stats.assigned}</StatValue>
            <StatLabel>Assigned</StatLabel>
          </StatBody>
        </StatCard>

        <StatCard $tone="#f09a3e">
          <StatIcon $tone="#f09a3e">
            <Wrench size={16} />
          </StatIcon>
          <StatBody>
            <StatValue>{stats.underService}</StatValue>
            <StatLabel>Under service</StatLabel>
          </StatBody>
        </StatCard>
      </StatsGrid>

      {(stats.serviceOverdue > 0 || stats.warrantyExpiringSoon > 0) && (
        <AlertBanner>
          <AlertIconWrap>
            <AlertTriangle size={20} />
          </AlertIconWrap>
          <div>
            <AlertTitle>Action Required</AlertTitle>
            <AlertDesc>
              You have {stats.serviceOverdue} item(s) with overdue servicing and{" "}
              {stats.warrantyExpiringSoon} item(s) with warranty expiring soon.
            </AlertDesc>
          </div>
        </AlertBanner>
      )}

      <Panel>
        <PanelTop>
          <div className="flex items-center gap-4 justify-between">
            <SearchControl>
              <Search size={16} />
              <input
                placeholder="Search by name, brand, model or serial number..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              />
            </SearchControl>
            <EntriesControl>
              <span>Show</span>
              <EntriesSelect value={entries} onChange={(e) => { setEntries(Number(e.target.value)); setCurrentPage(1); }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </EntriesSelect>
              <span>entries</span>
            </EntriesControl>
          </div>
          <ManageCategoriesBtn onClick={() => navigate("/dashboard/equipment/history")}>
            <History size={16} /> View history
          </ManageCategoriesBtn>
        </PanelTop>

        <Toolbar>
          <FiltersSelectRow>
            <Select
              value={category}
              onChange={(e) => { setCategory(e.target.value); setCurrentPage(1); }}
            >
              <option value="">All Categories</option>
              {categoriesList.map(cat => (
                <option key={cat.id} value={cat.name}>{cat.name}</option>
              ))}
            </Select>

            <Select
              value={type}
              onChange={(e) => { setType(e.target.value); setCurrentPage(1); }}
            >
              <option value="">All Types</option>
              <option value="Studio Owned">Studio Owned</option>
              <option value="Available for Rent">Available for Rent</option>
              <option value="Available for Sale">Available for Sale</option>
            </Select>

            <Select
              value={status}
              onChange={(e) => { setStatus(e.target.value); setCurrentPage(1); }}
            >
              <option value="">All Statuses</option>
              <option value="Available">Available</option>
              <option value="Assigned">Assigned</option>
              <option value="Under Service">Under Service</option>
              <option value="Sold">Sold</option>
              <option value="Retired">Retired</option>
            </Select>
          </FiltersSelectRow>
        </Toolbar>

        {loading ? (
          <MarketplaceLoadingSpinner padding="60px 22px" />
        ) : filteredList.length === 0 ? (
          <MarketplaceEmptyState
            padding="32px 22px 48px"
            icon={hasActiveFilters || !isEmptyInventory ? <SearchX size={36} strokeWidth={1.5} /> : <Camera size={36} strokeWidth={1.5} />}
            title={isEmptyInventory && !hasActiveFilters ? "No equipment yet" : "No equipment found"}
            description={
              isEmptyInventory && !hasActiveFilters
                ? "Start building your gear inventory — track cameras, lenses, assignments, servicing, and warranty details in one place."
                : "Nothing matched your search or filters. Try different keywords or broaden your criteria."
            }
            actions={[
              ...(hasActiveFilters
                ? [{
                  label: "Clear filters",
                  onClick: () => { setSearch(""); setCategory(""); setType(""); setStatus(""); setCurrentPage(1); },
                  variant: "secondary" as const,
                }]
                : []),
              ...(canCreate
                ? [{
                  label: isEmptyInventory && !hasActiveFilters ? "Add Your First Equipment" : "Add Equipment",
                  onClick: () => navigate("/dashboard/equipment/new"),
                  icon: <Plus size={15} />,
                }]
                : []),
            ]}
            hint={isEmptyInventory && !hasActiveFilters ? {
              icon: <Sparkles size={13} />,
              text: "Assign gear to team members & track service schedules",
            } : undefined}
          />
        ) : (
          <TableSection>
            <SectionHead>
              <ListTitle>All equipment</ListTitle>
              <CountBadge>
                {totalFiltered} item{totalFiltered !== 1 ? "s" : ""}
              </CountBadge>
            </SectionHead>

            <InventoryGrid>
              {paginatedList.map((item) => {
                const now = new Date();
                const serviceOverdue = item.nextServiceDueDate
                  ? new Date(item.nextServiceDueDate) < now
                  : false;
                const warrantyExpiringSoon = (() => {
                  if (!item.warrantyExpiry) return false;
                  const expDate = new Date(item.warrantyExpiry);
                  const diffDays = Math.ceil((expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                  return diffDays > 0 && diffDays <= 30;
                })();

                const hasAssigned = item.totalAssigned > 0;
                const canHandout = item.remainingTotal > 0 || item.totalAssigned > 0;
                const locked = item.status === "Sold" || item.status === "Retired";
                const isReturnMode = hasAssigned && item.remainingTotal === 0;
                const tone = statusTone[item.status] || "#c94daa";
                const freePct =
                  item.totalQuantity > 0
                    ? (item.remainingTotal / item.totalQuantity) * 100
                    : 0;
                const initials = (item.assignedMember || "?")
                  .split(" ")
                  .map((p) => p[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase();

                return (
                  <GearCard key={item.id} $tone={tone}>
                    <GearCardTop>
                      <GearTitleBlock>
                        <GearTitle>{item.name}</GearTitle>
                        <GearSubtitle>
                          {item.brand}
                          {item.model ? ` · ${item.model}` : ""}
                        </GearSubtitle>
                        <GearMetaLine>
                          {item.branch_name && (
                            <SoftChip style={{ color: "#c94daa", borderColor: "rgba(201, 77, 170, 0.3)" }}>
                              {item.branch_name}
                            </SoftChip>
                          )}
                          <SoftChip>{item.category}</SoftChip>
                          <SoftChip>{item.type}</SoftChip>
                        </GearMetaLine>
                      </GearTitleBlock>
                      <StatusBadge $status={item.status}>{item.status}</StatusBadge>
                    </GearCardTop>

                    <StockBlock>
                      <StockTrack>
                        <StockFill $pct={freePct} $tone={tone} />
                      </StockTrack>
                      <StockStats>
                        <StockStat>
                          <StockStatLabel>Total</StockStatLabel>
                          <StockStatValue>{item.totalQuantity}</StockStatValue>
                        </StockStat>
                        <StockStat>
                          <StockStatLabel>Out</StockStatLabel>
                          <StockStatValue>{item.totalAssigned}</StockStatValue>
                        </StockStat>
                        <StockStat>
                          <StockStatLabel>Free</StockStatLabel>
                          <StockStatValue>{item.remainingTotal}</StockStatValue>
                        </StockStat>
                      </StockStats>
                    </StockBlock>

                    <AssignBlock>
                      {hasAssigned ? (
                        <>
                          <AssignAvatar>{initials}</AssignAvatar>
                          <AssignText>
                            <AssignPrimary>{item.assignedMember || "Assigned"}</AssignPrimary>
                            <AssignSecondary>
                              {item.expectedReturnDate
                                ? `Due ${formatListDate(item.expectedReturnDate)}`
                                : `${item.totalAssigned} unit(s) currently out`}
                            </AssignSecondary>
                          </AssignText>
                        </>
                      ) : (
                        <AssignText>
                          <AssignPrimary>Ready to assign</AssignPrimary>
                          <AssignSecondary>
                            {item.remainingTotal} unit{item.remainingTotal !== 1 ? "s" : ""} available
                          </AssignSecondary>
                        </AssignText>
                      )}
                    </AssignBlock>

                    {(serviceOverdue || warrantyExpiringSoon) && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                        {serviceOverdue && (
                          <AlertDot $color="#e44862">
                            <AlertTriangle size={12} /> Service overdue
                          </AlertDot>
                        )}
                        {warrantyExpiringSoon && (
                          <AlertDot $color="#f09a3e">
                            <ShieldCheck size={12} /> Warranty ending soon
                          </AlertDot>
                        )}
                      </div>
                    )}

                    <GearCardActions>
                      {canUpdate && (
                        <PrimaryActionBtn
                          type="button"
                          $variant={isReturnMode ? "return" : "assign"}
                          disabled={locked || !canHandout}
                          onClick={() => setAssignTarget(item)}
                        >
                          {isReturnMode ? <CornerUpLeft size={15} /> : <UserPlus size={15} />}
                          {isReturnMode ? "Return" : "Assign"}
                        </PrimaryActionBtn>
                      )}
                      <SecondaryActions>
                        <QuietIconBtn
                          type="button"
                          aria-label="Item History"
                          data-tooltip="History"
                          onClick={() => navigate(`/dashboard/equipment/${item.id}/history`)}
                        >
                          <History size={15} />
                        </QuietIconBtn>
                        {canUpdate && (
                          <QuietIconBtn
                            type="button"
                            aria-label="Service Log"
                            data-tooltip="Service"
                            onClick={() => setServiceTarget(item)}
                          >
                            <Hammer size={15} />
                          </QuietIconBtn>
                        )}
                        {canUpdate && (
                          <QuietIconBtn
                            type="button"
                            aria-label="Edit"
                            data-tooltip="Edit"
                            onClick={() => navigate(`/dashboard/equipment/edit/${item.id}`)}
                          >
                            <Pencil size={15} />
                          </QuietIconBtn>
                        )}
                        {canDelete && (
                          <QuietIconBtn
                            type="button"
                            $danger
                            aria-label="Delete"
                            data-tooltip="Delete"
                            onClick={() => handleDelete(item.id, item.name)}
                          >
                            <Trash2 size={15} />
                          </QuietIconBtn>
                        )}
                      </SecondaryActions>
                    </GearCardActions>
                  </GearCard>
                );
              })}
            </InventoryGrid>
          </TableSection>
        )}

        {!loading && filteredList.length > 0 && <Divider />}

        <FooterRow>
          <FooterText>
            Showing {totalFiltered === 0 ? 0 : (currentPage - 1) * entries + 1} to{" "}
            {Math.min(currentPage * entries, totalFiltered)} of {totalFiltered} entries
          </FooterText>
          <Pagination>
            <PageButton disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>Previous</PageButton>
            {Array.from({ length: totalPages }).map((_, idx) => (
              <PageButton key={idx} $active={currentPage === idx + 1} onClick={() => setCurrentPage(idx + 1)}>{idx + 1}</PageButton>
            ))}
            <PageButton disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Next</PageButton>
          </Pagination>
        </FooterRow>
      </Panel>

      {assignTarget && (
        <AssignEquipmentModal
          equipment={assignTarget}
          onClose={() => setAssignTarget(null)}
          onUpdate={() => {
            fetchAllData();
            setAssignTarget(null);
          }}
        />
      )}

      {serviceTarget && (
        <ServiceHistoryModal
          equipment={serviceTarget}
          onClose={() => setServiceTarget(null)}
          onUpdate={() => {
            fetchAllData();
            setServiceTarget(null);
          }}
        />
      )}

      {showCategoriesModal && (
        <CategoryManagerModal
          onClose={() => setShowCategoriesModal(false)}
          onRefresh={fetchAllData}
        />
      )}

      {deleteTarget && (
        <DeletePopup
          text={`Are you sure you want to delete "${deleteTarget.name}"?`}
          buttonText="Delete"
          cancel={() => setDeleteTarget(null)}
          Delete={handleConfirmDelete}
        />
      )}
    </PageContainer>
  );
};

export default EquipmentListPage;
